import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('fullName', formData.fullName);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d517oplpfatkmn6ifbhg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ fullName: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative overflow-hidden">
      <div className="flex flex-col lg:flex-row min-h-[600px]">
        <div className="lg:w-[45%] bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 relative">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-32 h-32 border-2 border-white rounded-full"></div>
            <div className="absolute bottom-20 right-10 w-24 h-24 border-2 border-white"></div>
            <div className="absolute top-1/2 left-1/4 w-16 h-16 border-2 border-white rotate-45"></div>
          </div>
          
          <div className="relative h-full flex flex-col justify-center px-12 py-16">
            <h2 className="text-5xl font-bold text-white mb-12">
              Get in Touch
            </h2>
            
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-purple-300 text-sm mb-1">Email Address</p>
                  <a href="mailto:information@openstudiodoo.com" className="text-white text-lg hover:text-purple-300 transition-colors cursor-pointer">
                    information@openstudiodoo.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-purple-300 text-sm mb-1">Phone Number</p>
                  <a href="tel:+12137533391" className="text-white text-lg hover:text-purple-300 transition-colors cursor-pointer">
                    +1 213 753 3391
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-purple-300 text-sm mb-1">Office Location</p>
                  <p className="text-white text-lg">
                    Put Vrela 74c<br />
                    23000 Zadar, Croatia
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:w-[55%] bg-white">
          <div className="h-full flex items-center px-12 py-16">
            <form onSubmit={handleSubmit} className="w-full max-w-xl" data-readdy-form>
              <div className="space-y-6">
                <div>
                  <input
                    type="text"
                    name="fullName"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    required
                    className="w-full bg-transparent border-b-2 border-gray-300 focus:border-purple-600 outline-none py-4 text-base text-gray-900 placeholder-gray-400 transition-colors"
                  />
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full bg-transparent border-b-2 border-gray-300 focus:border-purple-600 outline-none py-4 text-base text-gray-900 placeholder-gray-400 transition-colors"
                  />
                </div>

                <div>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    required
                    className="w-full bg-transparent border-b-2 border-gray-300 focus:border-purple-600 outline-none py-4 text-base text-gray-900 transition-colors cursor-pointer"
                  >
                    <option value="">Select Subject</option>
                    <option value="general">General Inquiry</option>
                    <option value="support">Technical Support</option>
                    <option value="sales">Sales Question</option>
                    <option value="partnership">Partnership Opportunity</option>
                  </select>
                </div>

                <div>
                  <textarea
                    name="message"
                    placeholder="Your Message"
                    value={formData.message}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setFormData({ ...formData, message: e.target.value });
                      }
                    }}
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full bg-transparent border-b-2 border-gray-300 focus:border-purple-600 outline-none py-4 text-base text-gray-900 placeholder-gray-400 transition-colors resize-none"
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-2">{formData.message.length}/500 characters</p>
                </div>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                    Thank you! Your message has been sent successfully.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                    Something went wrong. Please try again.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white py-4 rounded-full text-base font-medium hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
